import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Save } from "lucide-react";
import { toast } from "react-toastify";

import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomInputNumber from "@/components/desktop/form/input/input-number/CustomInputNumber";
import Fetching from "@/components/Fetching/Fetching";

import { useOAPrices, useSaveOAPrices } from "@/hooks/planning/oa/useOA";
import useOAListQueryFilters from "@/hooks/planning/oa/useOAListQueryFilters";
import { patchOAListCacheAfterPriceSave } from "@/hooks/planning/oa/oaListCache";
import { getProductPriceUnitLabel } from "@/utils/unitFormatter";

import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";

import styles from "./modal-oa-recalculate-prices.module.scss";

const groupOAPriceLines = (lines = []) => {
	const groupedByProductId = new Map();

	lines.forEach((line) => {
		const productId = Number(line.product_id);
		if (!productId) {
			return;
		}

		const existingLine = groupedByProductId.get(productId);
		if (!existingLine) {
			groupedByProductId.set(productId, line);
			return;
		}

		groupedByProductId.set(productId, {
			...existingLine,
			current_price: Math.max(Number(existingLine.current_price ?? 0), Number(line.current_price ?? 0)),
			suggested_price: Number(existingLine.suggested_price ?? 0) > 0 ? Number(existingLine.suggested_price) : Number(line.suggested_price ?? 0),
		});
	});

	return Array.from(groupedByProductId.values());
};

const buildInitialPrices = (lines = []) => {
	const initialPrices = {};
	lines.forEach((line) => {
		const suggestedPrice = Number(line.suggested_price ?? 0);
		const currentPrice = Number(line.current_price ?? 0);
		initialPrices[line.product_id] = suggestedPrice > 0 ? suggestedPrice : currentPrice;
	});
	return initialPrices;
};

const ModalOARecalculatePrices = ({ code, onClose }) => {
	const location = useLocation();
	const queryClient = useQueryClient();
	const listFilters = useOAListQueryFilters();
	const { data, isLoading, isFetching, isError, error } = useOAPrices(code);
	const { mutate: savePrices, isLoading: isSaving } = useSaveOAPrices();
	const [priceByProductId, setPriceByProductId] = useState({});

	useEffect(() => {
		if (!data?.lines) {
			return;
		}
		setPriceByProductId(buildInitialPrices(groupOAPriceLines(data.lines)));
	}, [data]);

	const handlePriceChange = useCallback((productId, nextPrice) => {
		setPriceByProductId((previousState) => ({
			...previousState,
			[productId]: nextPrice ?? 0,
		}));
	}, []);

	const lines = useMemo(() => groupOAPriceLines(data?.lines || []), [data?.lines]);
	const stateVariant = getApplicationOrderStateVariant(Number(data?.state_id));
	const loadErrorMessage = error?.response?.data?.messages?.[0] || error?.response?.data?.errors?.[0] || error?.response?.data?.error || null;

	const savePayloadLines = useMemo(
		() =>
			lines.map((line) => ({
				product_id: line.product_id,
				price: Number(priceByProductId[line.product_id] ?? 0),
			})),
		[lines, priceByProductId],
	);

	const handleSave = useCallback(() => {
		const applicationOrderId = Number(data?.oa_id || code);
		if (!applicationOrderId || lines.length === 0) {
			return;
		}

		const hasInvalidPrice = savePayloadLines.some((line) => !Number.isFinite(line.price) || line.price < 0);
		if (hasInvalidPrice) {
			toast.error("Revise los precios ingresados antes de guardar.");
			return;
		}

		savePrices(
			{
				oa_id: applicationOrderId,
				lines: savePayloadLines,
				filters: listFilters,
			},
			{
				onSuccess: (response) => {
					patchOAListCacheAfterPriceSave(queryClient, code, response);
					if (location.pathname.startsWith("/ordenes_aplicacion")) {
						queryClient.invalidateQueries(["application_orders"]);
					}
					toast.success(`Se actualizaron los precios de la OA N° ${data?.code || code}.`);
					onClose();
				},
				onError: (mutationError) => {
					const serverMessage = mutationError?.response?.data?.errors?.[0];
					toast.error(serverMessage || "Error al guardar los precios de la OA.");
				},
			},
		);
	}, [code, data?.code, data?.oa_id, lines.length, listFilters, location.pathname, onClose, queryClient, savePayloadLines, savePrices]);

	if (!code) {
		return null;
	}

	return (
		<CustomModalForm
			open={Boolean(code)}
			onClose={onClose}
			title={`Precios OA N° ${data?.code || ""}`}
			size="md"
			closeOnBackdrop={false}
			closeOnEsc={!isSaving}
			showSave={false}
			gridTable={false}
			processingMessage={isSaving ? "Guardando precios..." : ""}
			footer={
				<>
					<CustomButton variant="secondary" onClick={onClose} disabled={isSaving}>
						Cancelar
					</CustomButton>
					<CustomButton
						variant="primary"
						startIcon={<Save size={16} />}
						onClick={handleSave}
						loading={isSaving}
						disabled={isLoading || lines.length === 0}
					>
						Guardar precios
					</CustomButton>
				</>
			}
		>
			{isLoading || isFetching ? (
				<Fetching newStyles={{ marginTop: "1.5em", marginBottom: "1.5em" }} />
			) : isError ? (
				<div className={styles.emptyState}>{loadErrorMessage || "No se pudieron cargar los precios de la OA."}</div>
			) : (
				<div className={styles.content}>
					<div className={styles.headerCard}>
						<div className={styles.headerTop}>
							<span className={styles.headerBadge}>Lista de precios</span>
							<div className={styles.metaRow}>
								<CustomBadge variant={stateVariant} size="sm">
									{data?.state_name || "Sin estado"}
								</CustomBadge>
								<span className={styles.metaDivider} />
								<span className={styles.metaEstate}>{data?.estate_name || "Sin bodega"}</span>
							</div>
						</div>
						<p className={styles.headerHint}>Precios sugeridos del mantenedor de productos. Al guardar, se aplican a esta OA.</p>
					</div>

					{lines.length === 0 ? (
						<div className={styles.emptyState}>No hay productos en el detalle de esta OA.</div>
					) : (
						<ul className={styles.productList}>
							{lines.map((line) => {
								const unitLabel = getProductPriceUnitLabel(line.unit_measurement);
								const suggestedPrice = Number(line.suggested_price ?? 0);
								const currentValue = Number(priceByProductId[line.product_id] ?? 0);
								const isEdited = suggestedPrice > 0 && currentValue !== suggestedPrice;

								return (
									<li key={line.product_id} className={`${styles.productRow} ${isEdited ? styles.productRowEdited : ""}`}>
										<div className={styles.productInfo}>
											<span className={styles.productName} title={line.product_name}>
												{line.product_name || "-"}
											</span>
											{isEdited && <span className={styles.editedHint}>Precio ajustado</span>}
										</div>
										<div className={styles.priceField}>
											<CustomInputNumber
												name={`price-${line.product_id}`}
												value={priceByProductId[line.product_id]}
												onChange={(nextPrice) => handlePriceChange(line.product_id, nextPrice)}
												placeholder="0"
												size="sm"
												col={12}
												disabled={isSaving}
												addonBefore="$"
												addonAfter={`por ${unitLabel}`}
												fixedWidthUnitSuffix
												className={styles.priceInput}
											/>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</CustomModalForm>
	);
};

export default ModalOARecalculatePrices;
